import React, { useRef, useCallback, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button as MuiButton } from '@mui/material';
import moment from 'moment';
import {
  Actions,
  CanvasStyleHelper,
  CanvasWrapper,
  canvasClass,
  Container,
  Label,
  Preview,
  PreviewImage,
  SaveButton,
  Timestamp,
} from './signature-pad.styles';

const SignaturePad = ({ value, onChange, error }) => {
  const sigRef = useRef(null);
  const wrapperRef = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !sigRef.current) return;

    const observer = new ResizeObserver(() => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const displayW = canvas.offsetWidth;
      const displayH = canvas.offsetHeight;
      if (canvas.width !== displayW * ratio || canvas.height !== displayH * ratio) {
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        canvas.width = displayW * ratio;
        canvas.height = displayH * ratio;
        ctx.scale(ratio, ratio);
        ctx.putImageData(imageData, 0, 0);
      }
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [value]);

  const handleEnd = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setHasStrokes(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
    onChange({
      data: dataUrl,
      timestamp: new Date().toISOString(),
    });
  }, [onChange]);

  const handleClear = useCallback(() => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    setHasStrokes(false);
    onChange(null);
  }, [onChange]);

  const hasSigned = value && value.data;

  return (
    <Container hasError={!!error}>
      <Label>{`* Sign below`}</Label>
      {!hasSigned ? (
        <CanvasStyleHelper>
          <CanvasWrapper ref={wrapperRef}>
            <SignatureCanvas
              ref={sigRef}
              penColor="black"
              canvasProps={{ className: canvasClass }}
              onEnd={handleEnd}
            />
          </CanvasWrapper>
        </CanvasStyleHelper>
      ) : (
        <Preview>
          <PreviewImage src={value.data} alt="Signature" />
          <Timestamp>
            {`Signed ${moment(value.timestamp).format('L hh:mma')}`}
          </Timestamp>
        </Preview>
      )}
      <Actions>
        <MuiButton size="small" onClick={handleClear} disabled={!hasSigned && !hasStrokes}>
          Clear
        </MuiButton>
        {!hasSigned && (
          <SaveButton size="small" variant="contained" color="primary" onClick={handleSave} disabled={!hasStrokes}>
            Save
          </SaveButton>
        )}
      </Actions>
    </Container>
  );
};

export default SignaturePad;
